import { useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import styles from './Modal.module.css';

/**
 * Modal component with compound pattern for flexible structure.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is visible
 * @param {Function} props.onClose - Called when modal should close
 * @param {'sm' | 'md' | 'lg' | 'xl' | 'full'} [props.size='md'] - Modal width
 * @param {boolean} [props.closeOnOverlay=true] - Close when clicking overlay
 * @param {boolean} [props.closeOnEscape=true] - Close on Escape key
 * @param {string} [props.className] - Additional CSS classes
 * @param {React.ReactNode} props.children - Modal content
 *
 * @example
 * <Modal isOpen={show} onClose={() => setShow(false)}>
 *   <Modal.Header>Title</Modal.Header>
 *   <Modal.Body>Content here</Modal.Body>
 *   <Modal.Footer>
 *     <Button onClick={() => setShow(false)}>Close</Button>
 *   </Modal.Footer>
 * </Modal>
 */
export default function Modal({
  isOpen,
  onClose,
  size = 'md',
  closeOnOverlay = true,
  closeOnEscape = true,
  className,
  children,
}) {
  const overlayRef = useRef(null);
  const modalRef = useRef(null);

  const handleKeyDown = useCallback(
    (event) => {
      if (closeOnEscape && event.key === 'Escape') {
        onClose();
      }
    },
    [closeOnEscape, onClose]
  );

  const handleOverlayClick = useCallback(
    (event) => {
      if (closeOnOverlay && event.target === overlayRef.current) {
        onClose();
      }
    },
    [closeOnOverlay, onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';

      // Focus first focusable element for accessibility
      const firstFocusable = modalRef.current?.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      firstFocusable?.focus();
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={overlayRef}
      className={styles.overlay}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
    >
      <div ref={modalRef} className={clsx(styles.modal, styles[size], className)}>
        {children}
      </div>
    </div>,
    document.body
  );
}

/**
 * Modal header with optional close button.
 */
function ModalHeader({ children, showClose = true, onClose, className }) {
  return (
    <div className={clsx(styles.header, className)}>
      <h2 className={styles.title}>{children}</h2>
      {showClose && (
        <button
          type="button"
          onClick={onClose}
          className={styles.closeButton}
          aria-label="Close modal"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      )}
    </div>
  );
}

/**
 * Modal body for main content.
 */
function ModalBody({ children, className }) {
  return <div className={clsx(styles.body, className)}>{children}</div>;
}

/**
 * Modal footer for actions.
 */
function ModalFooter({ children, className }) {
  return <div className={clsx(styles.footer, className)}>{children}</div>;
}

// Attach compound components
Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
